// Reads an IMAGE off the system clipboard on demand — the "Paste" button in
// the two upload boxes (ImageUploadBox, LocalImageDropBox).
//
// Why: every upload field already accepts Ctrl/Cmd+V, but a phone has no
// keyboard shortcut, so phone users were forced through "choose a file" even
// when the screenshot was already copied (operator report 2026-10-12). This is
// the tap equivalent of the keyboard paste, built on the Async Clipboard API.
//
// Browser rules worth knowing:
//  - navigator.clipboard.read() only works from a user gesture, so it must be
//    called synchronously from the click handler (this helper calls it before
//    its first await — keep it that way).
//  - iOS Safari shows a small "Paste" bubble the user must tap; Android Chrome
//    asks once for clipboard permission; Firefox shows a "Paste" context
//    button. Dismissing any of them rejects with NotAllowedError.
//  - Chrome rejects with DataError when the clipboard holds nothing it can
//    hand over (e.g. only an app-private format) — reported as "empty".
export const canReadClipboard = () =>
  typeof navigator !== 'undefined'
  && !!navigator.clipboard
  && typeof navigator.clipboard.read === 'function';

export const CLIPBOARD_MESSAGES = {
  unsupported: 'This browser cannot read the clipboard. Use "Select file" instead.',
  denied: 'Clipboard access was not allowed. Tap Paste again and choose Allow / Paste when the browser asks, or use "Select file".',
  empty: 'No image in the clipboard. Copy an image (or take a screenshot and copy it) first, then tap Paste.',
  failed: 'Could not read the clipboard. Try again, or use "Select file".',
};

const extensionFor = (mime) => {
  const sub = (mime.split('/')[1] || 'png').split('+')[0].split(';')[0];
  return sub === 'jpeg' ? 'jpg' : sub;
};

const stamp = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
};

// Resolves to { file } (a File, same shape the paste event's getAsFile()
// gives) or { error: 'unsupported' | 'denied' | 'empty' | 'failed' }.
export const readClipboardImage = async () => {
  if (!canReadClipboard()) return { error: 'unsupported' };
  let items;
  try {
    items = await navigator.clipboard.read();
  } catch (err) {
    const name = err && err.name;
    if (name === 'NotAllowedError' || name === 'SecurityError') return { error: 'denied' };
    if (name === 'DataError') return { error: 'empty' };
    return { error: 'failed' };
  }
  for (const item of items || []) {
    const type = (item.types || []).find((t) => typeof t === 'string' && t.startsWith('image/'));
    if (!type) continue;
    try {
      const blob = await item.getType(type);
      const mime = blob.type || type;
      return { file: new File([blob], `pasted-${stamp()}.${extensionFor(mime)}`, { type: mime }) };
    } catch (err) {
      return { error: 'failed' };
    }
  }
  return { error: 'empty' };
};
